// src/pages/DriverPerformance.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

function DriverPerformance() {
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('week');
  const navigate = useNavigate();

  const API_URL = 'https://boda-backend-wuea.onrender.com/api/admin';

  useEffect(() => {
    fetchDrivers();
  }, []);

  useEffect(() => {
    if (selectedDriver) {
      fetchPerformanceData(selectedDriver.id);
    }
  }, [selectedDriver, timeRange]);

  const fetchDrivers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/all-drivers`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      setDrivers(response.data.drivers || []);
      if (response.data.drivers && response.data.drivers.length > 0) {
        setSelectedDriver(response.data.drivers[0]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching drivers:', error);
      setLoading(false);
    }
  };

  const fetchPerformanceData = async (driverId) => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch driver rides
      const response = await axios.get(
        `https://boda-backend-wuea.onrender.com/api/driver/rides/${driverId}`,
        { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }
      );
      
      const rides = response.data.rides || [];
      
      // Calculate performance metrics
      const totalRides = rides.length;
      const completedRides = rides.filter(r => r.status === 'completed').length;
      const cancelledRides = rides.filter(r => r.status === 'cancelled').length;
      const inProgressRides = rides.filter(r => r.status === 'in_progress').length;
      
      const totalEarnings = rides
        .filter(r => r.status === 'completed')
        .reduce((sum, r) => sum + (r.total_fare || 0), 0);
      
      const totalCommission = rides
        .filter(r => r.status === 'completed')
        .reduce((sum, r) => sum + (r.commission || 0), 0);
      
      const ratingSum = rides
        .filter(r => r.rating)
        .reduce((sum, r) => sum + (r.rating || 0), 0);
      const avgRating = ratingSum > 0 ? ratingSum / rides.filter(r => r.rating).length : 0;
      
      const completionRate = totalRides > 0 ? (completedRides / totalRides * 100) : 0;
      
      // Get last 7 days rides
      const today = new Date();
      const last7Days = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        const dayRides = rides.filter(r => r.created_at?.startsWith(dateStr));
        last7Days.push({
          date: dateStr,
          rides: dayRides.length,
          earnings: dayRides.reduce((sum, r) => sum + (r.total_fare || 0), 0),
          completed: dayRides.filter(r => r.status === 'completed').length,
        });
      }
      
      setPerformanceData({
        driverId: driverId,
        totalRides,
        completedRides,
        cancelledRides,
        inProgressRides,
        totalEarnings,
        totalCommission,
        avgRating,
        completionRate,
        dailyStats: last7Days,
        recentRides: rides.slice(0, 10),
      });
      
    } catch (error) {
      console.error('Error fetching performance data:', error);
    }
  };

  const getDriverName = (driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    return driver?.full_name || `Driver ${driverId}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#10b981';
      case 'in_progress': return '#f59e0b';
      case 'pending': return '#f59e0b';
      case 'cancelled': return '#ef4444';
      case 'accepted': return '#3b82f6';
      default: return '#64748b';
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>🔄</div>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Loading performance data...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 20px 0' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Driver Performance</h1>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <select
            value={selectedDriver?.id || ''}
            onChange={(e) => {
              const driver = drivers.find(d => d.id === parseInt(e.target.value));
              setSelectedDriver(driver);
            }}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              backgroundColor: 'white',
              fontSize: '14px',
              cursor: 'pointer',
              minWidth: '180px',
            }}
          >
            {drivers.map(d => (
              <option key={d.id} value={d.id}>
                {d.full_name || `Driver ${d.id}`}
              </option>
            ))}
          </select>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              backgroundColor: 'white',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="quarter">Last 90 Days</option>
          </select>
        </div>
      </div>

      {performanceData ? (
        <>
          {/* Stats Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '16px',
            marginBottom: '24px',
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '16px 20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Rides</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#f97316' }}>
                {performanceData.totalRides}
              </div>
            </div>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '16px 20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Completed</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#10b981' }}>
                {performanceData.completedRides}
              </div>
            </div>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '16px 20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Completion Rate</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#3b82f6' }}>
                {performanceData.completionRate.toFixed(1)}%
              </div>
            </div>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '16px 20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Avg Rating</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#f59e0b' }}>
                ⭐ {performanceData.avgRating.toFixed(1)}
              </div>
            </div>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '16px 20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Earnings</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#10b981' }}>
                UGX {performanceData.totalEarnings.toLocaleString()}
              </div>
            </div>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '16px 20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Commission</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#ef4444' }}>
                UGX {performanceData.totalCommission.toLocaleString()}
              </div>
            </div>
          </div>

          {/* Daily Trend */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}>
            <h3 style={{ marginTop: 0 }}>Daily Trend</h3>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', height: '120px', padding: '10px 0' }}>
              {performanceData.dailyStats.map((day, index) => {
                const max = Math.max(...performanceData.dailyStats.map(d => d.rides), 1);
                const height = (day.rides / max * 100);
                return (
                  <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{
                      width: '100%',
                      height: `${Math.max(height, 4)}%`,
                      backgroundColor: day.completed === day.rides ? '#10b981' : '#f97316',
                      borderRadius: '4px',
                      minHeight: '4px',
                      transition: 'height 0.3s',
                    }} />
                    <div style={{ fontSize: '9px', marginTop: '4px', color: '#64748b' }}>
                      {new Date(day.date).toLocaleDateString('en', { weekday: 'short' })}
                    </div>
                    <div style={{ fontSize: '8px', fontWeight: 'bold' }}>{day.rides}</div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
              <span>📊 Total rides: {performanceData.totalRides}</span>
              <span>💰 Total earnings: UGX {performanceData.totalEarnings.toLocaleString()}</span>
            </div>
          </div>

          {/* Status Breakdown */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px',
            marginBottom: '24px',
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <h3 style={{ marginTop: 0 }}>Ride Status Breakdown</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { label: 'Completed', count: performanceData.completedRides, color: '#10b981' },
                  { label: 'In Progress', count: performanceData.inProgressRides, color: '#f59e0b' },
                  { label: 'Cancelled', count: performanceData.cancelledRides, color: '#ef4444' },
                ].map(item => {
                  const percentage = performanceData.totalRides > 0 ? (item.count / performanceData.totalRides * 100) : 0;
                  return (
                    <div key={item.label}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                        <span>{item.label}</span>
                        <span>{item.count} ({percentage.toFixed(0)}%)</span>
                      </div>
                      <div style={{
                        width: '100%',
                        height: '6px',
                        backgroundColor: '#e2e8f0',
                        borderRadius: '4px',
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          width: `${percentage}%`,
                          height: '100%',
                          backgroundColor: item.color,
                          borderRadius: '4px',
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <h3 style={{ marginTop: 0 }}>Recent Rides</h3>
              {performanceData.recentRides.length === 0 ? (
                <p style={{ color: '#64748b', textAlign: 'center', padding: '20px' }}>No recent rides</p>
              ) : (
                <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                  {performanceData.recentRides.map((ride, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 0',
                        borderBottom: index < performanceData.recentRides.length - 1 ? '1px solid #f1f5f9' : 'none',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: '500' }}>Ride #{ride.id}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>
                          {ride.pickup_address?.substring(0, 20)} → {ride.dropoff_address?.substring(0, 20)}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#f97316' }}>
                          UGX {ride.total_fare?.toLocaleString()}
                        </div>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '10px',
                          backgroundColor: getStatusColor(ride.status) + '20',
                          color: getStatusColor(ride.status),
                        }}>
                          {ride.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
          }}>
            <button
              onClick={() => selectedDriver && navigate(`/drivers/${selectedDriver.id}`)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              👤 View Driver Profile
            </button>
            <button
              onClick={() => {
                const data = [
                  ['Metric', 'Value'],
                  ['Total Rides', performanceData.totalRides],
                  ['Completed', performanceData.completedRides],
                  ['Cancelled', performanceData.cancelledRides],
                  ['Completion Rate', performanceData.completionRate.toFixed(1) + '%'],
                  ['Avg Rating', performanceData.avgRating.toFixed(1)],
                  ['Total Earnings', performanceData.totalEarnings],
                  ['Commission', performanceData.totalCommission],
                ];
                // Simple CSV export
                const csv = data.map(row => row.join(',')).join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `driver_performance_${selectedDriver?.id || 'all'}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              📥 Export Report
            </button>
            <button
              onClick={() => {
                // Refresh data
                if (selectedDriver) {
                  fetchPerformanceData(selectedDriver.id);
                }
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#f97316',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              🔄 Refresh
            </button>
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
          <p>No performance data available for this driver</p>
        </div>
      )}
    </div>
  );
}

export default DriverPerformance;